import { Mail, MapPin, Phone, ShieldCheck } from "lucide-react";
import Seo from "@/components/Seo";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { EnquiryForm } from "@/components/EnquiryForm";
import { CONTACT } from "@/data/content";

export default function Contact() {
  return (
    <>
      <Seo
        title="Book a free consultation — Brightside Befriending Services, Glasgow"
        siteName="Brightside Befriending Services"
        description="Book a free, no-obligation consultation with Brightside Befriending Services. Private companionship for older adults across Glasgow and surrounding areas."
      />
      <PageHero
        eyebrow="Contact us"
        lines={["Let's start with", "a friendly chat."]}
        text="Tell us a little about the person you have in mind. We'll reply within one working day — and the consultation itself is always free, with no obligation at all."
      />

      <section className="px-4 pb-24 sm:px-6 lg:px-8" data-testid="contact-section">
        <div className="mx-auto grid max-w-7xl gap-10 lg:grid-cols-5">
          <Reveal className="lg:col-span-3">
            <div className="rounded-[2.5rem] border border-sand bg-white p-8 shadow-[0_16px_40px_rgba(28,56,41,0.06)] sm:p-12">
              <h2 className="font-serif text-2xl text-forest">Book your free consultation</h2>
              <p className="mt-2 text-sm text-pebble">A real person reads every message — usually the founder.</p>
              <div className="mt-8">
                <EnquiryForm kind="consultation" showScheduling buttonLabel="Request my free consultation" testIdPrefix="contact" />
              </div>
            </div>
          </Reveal>

          <div className="space-y-6 lg:col-span-2">
            <Reveal delay={0.1}>
              <div className="rounded-[2.5rem] bg-forest p-8 text-linen sm:p-10" data-testid="contact-details-card">
                <h3 className="font-serif text-xl">Other ways to reach us</h3>
                <ul className="mt-6 space-y-5 text-sm">
                  <li className="flex items-start gap-3.5">
                    <Mail className="mt-0.5 h-4 w-4 shrink-0 text-terracotta-soft" />
                    <span>
                      <span className="block text-xs uppercase tracking-widest text-linen/50">Email</span>
                      <a data-testid="contact-email-link" href={`mailto:${CONTACT.email}`} className="transition-colors hover:text-terracotta-soft">{CONTACT.email}</a>
                    </span>
                  </li>
                  <li className="flex items-start gap-3.5">
                    <Phone className="mt-0.5 h-4 w-4 shrink-0 text-terracotta-soft" />
                    <span>
                      <span className="block text-xs uppercase tracking-widest text-linen/50">Phone</span>
                      <span data-testid="contact-phone">{CONTACT.phone} <span className="text-linen/50">({CONTACT.phoneNote} · {CONTACT.hours})</span></span>
                      <span data-testid="contact-mobile" className="mt-1 block">{CONTACT.mobile} <span className="text-linen/50">({CONTACT.mobileNote})</span></span>
                    </span>
                  </li>
                  <li className="flex items-start gap-3.5">
                    <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-terracotta-soft" />
                    <span>
                      <span className="block text-xs uppercase tracking-widest text-linen/50">Registered office</span>
                      <span data-testid="contact-address">{CONTACT.address}</span>
                    </span>
                  </li>
                </ul>
                <div className="mt-8 flex items-center gap-3 rounded-2xl border border-linen/15 bg-forest-deep/50 p-4">
                  <ShieldCheck className="h-5 w-5 shrink-0 text-terracotta-soft" />
                  <p className="text-xs leading-relaxed text-linen/70">Every befriender is PVG checked and vetted before their first visit.</p>
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.18}>
              <div className="rounded-[2.5rem] border border-sand bg-linen-deep p-8 sm:p-10" data-testid="contact-coverage-card">
                <h3 className="font-serif text-xl text-forest">Where we visit</h3>
                <ul className="mt-5 space-y-2.5 text-sm text-pebble">
                  {CONTACT.areas.map((area) => (
                    <li key={area} className="flex items-center gap-2.5">
                      <MapPin className="h-3.5 w-3.5 text-terracotta" /> {area}
                    </li>
                  ))}
                </ul>
                <p className="mt-5 text-xs leading-relaxed text-pebble/70">Just outside these areas? Ask anyway — we're growing quickly.</p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
